<template>
  <div v-if="imageUrl && !imageError" class="image-block" :class="[alignmentClass, containerWidthClass]">
    <figure class="image-wrapper" :style="{ maxWidth: maxWidthPx }">
      <div class="image-container">
        <img
          ref="img"
          :src="displaySrc"
          :alt="altText"
          class="block-image"
          :class="{ 'is-loaded': imageLoaded }"
          :loading="eager ? 'eager' : 'lazy'"
          :fetchpriority="eager ? 'high' : 'auto'"
          :width="data.imageWidth || undefined"
          :height="data.imageHeight || undefined"
          @load="imageLoaded = true"
          @error="handleImageError"
        >
      </div>
      <figcaption v-if="caption" class="image-caption">
        {{ caption }}
      </figcaption>
    </figure>
  </div>
</template>

<script>
import { wrapperToDirectUrl } from '@/utils/imageUrl'

export default {
  name: 'ImageBlock',
  props: {
    data: {
      type: Object,
      required: true
    },
    // First block on the page: load eagerly + high priority (above the fold).
    eager: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageError: false,
      imageLoaded: false,
      directFailed: false // direct GCS URL 403'd - fall back to the wrapper URL
    }
  },
  computed: {
    imageUrl() {
      return this.data.imageUrl || ''
    },
    // Prefer the fast direct GCS URL; on its error, fall back to the original wrapper URL.
    displaySrc() {
      if (this.directFailed) return this.imageUrl
      return wrapperToDirectUrl(this.imageUrl) || this.imageUrl
    },
    altText() {
      return this.data.altText || this.data.caption || 'Image'
    },
    caption() {
      return this.data.caption || ''
    },
    alignment() {
      return this.data.alignment || 'center'
    },
    alignmentClass() {
      return `align-${this.alignment}`
    },
    maxWidth() {
      // Max width in percentage or pixels (default: 100%)
      return this.data.maxWidth || 100
    },
    containerWidthClass() {
      return this.data.containerWidth === 'content' ? 'image-width-content' : 'image-width-full'
    },
    maxWidthPx() {
      // If maxWidth is a number, treat as percentage
      if (typeof this.maxWidth === 'number') {
        return `${this.maxWidth}%`
      }
      // Otherwise return as-is (e.g., "600px")
      return this.maxWidth
    }
  },
  watch: {
    'data.imageUrl'() {
      this.imageError = false
      this.directFailed = false
    }
  },
  mounted() {
    if (!this.imageUrl) {
      console.error('[ImageBlock] Missing imageUrl — block will not render.')
      return
    }
    // Handle already-cached images where @load fires before mount
    this.$nextTick(() => {
      if (this.$refs.img?.complete) this.imageLoaded = true
    })
  },
  methods: {
    handleImageError() {
      // First failure on the direct GCS URL: retry with the wrapper URL before giving up.
      if (!this.directFailed && this.displaySrc !== this.imageUrl) {
        this.directFailed = true
        return
      }
      this.imageError = true
      console.error('[ImageBlock] Failed to load image:', this.imageUrl)
    }
  }
}
</script>

<style scoped>
.image-block {
  padding: 0;
  width: 100%;
  margin: 0 auto;

  &.image-width-full { max-width: 1200px; }
  &.image-width-content { max-width: 800px; }


  &.align-left {
    text-align: left;
    .image-wrapper {
      margin-left: 0;
      margin-right: auto;
    }
  }

  &.align-center {
    text-align: center;
    .image-wrapper {
      margin-left: auto;
      margin-right: auto;
    }
  }

  &.align-right {
    text-align: right;
    .image-wrapper {
      margin-left: auto;
      margin-right: 0;
    }
  }
}

.image-wrapper {
  display: block;
  max-width: 100%;
  margin: 0;
}

.image-container {
  background: color-mix(in srgb, var(--color-text) 6%, var(--color-bg));
}

.block-image {
  width: 100%;
  height: auto;
  display: block;
  opacity: 0;
  transition: opacity 0.2s ease;

  &.is-loaded {
    opacity: 1;
  }
}


.image-caption {
  margin-top: 12px;
  padding: 0 10px;
  font-size: var(--font-sm);
  color: color-mix(in srgb, var(--color-text) 75%, var(--color-bg));
  font-style: italic;
  line-height: 1.4;
  text-align: center;
}
</style>
